/**
 * Scheduling a mass email: the editor's state, what it posts, how it reads.
 *
 * Editor state (camelCase) is what the form holds:
 *   repeat     "none" | "daily" | "weekly" | "monthly"
 *   interval   every N days / weeks / months
 *   weekdays   0 = Monday ... 6 = Sunday, for weekly
 *   startDate  "YYYY-MM-DD", sendTime "HH:MM" in club (Eastern) time
 *   ends       "never" | "on" (endDate) | "after" (maxRuns sends)
 *
 * The server owns the real rule (app/services/email_schedule.py) and
 * re-checks everything; the checks here only save a round trip.
 */

export const CLUB_TIMEZONE_LABEL = "Eastern Time";

export const WEEKDAYS = [
  { value: 0, short: "Mon" },
  { value: 1, short: "Tue" },
  { value: 2, short: "Wed" },
  { value: 3, short: "Thu" },
  { value: 4, short: "Fri" },
  { value: 5, short: "Sat" },
  { value: 6, short: "Sun" },
];

const UNIT = { daily: "day", weekly: "week", monthly: "month" };

const pad = (n) => String(n).padStart(2, "0");
const localIsoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** A fresh schedule: tomorrow at 7:00 AM, once. */
export const defaultSchedule = (today = new Date()) => {
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return {
    repeat: "none",
    interval: 1,
    weekdays: [],
    startDate: localIsoDate(tomorrow),
    sendTime: "07:00",
    ends: "never",
    endDate: "",
    maxRuns: "",
  };
};

const sortedDays = (weekdays) => [...new Set(weekdays)].sort((a, b) => a - b);

/** Form fields for POST /admin/bulk/email/schedule. */
export const scheduleFields = (s) => {
  const fields = [
    ["repeat", s.repeat],
    ["interval", String(s.repeat === "none" ? 1 : s.interval || 1)],
    ["start_date", s.startDate],
    ["send_time", s.sendTime],
  ];
  if (s.repeat === "weekly") {
    fields.push(["weekdays", JSON.stringify(sortedDays(s.weekdays))]);
  }
  if (s.repeat !== "none" && s.ends === "on" && s.endDate) {
    fields.push(["end_date", s.endDate]);
  }
  if (s.repeat !== "none" && s.ends === "after" && s.maxRuns) {
    fields.push(["max_runs", String(s.maxRuns)]);
  }
  return fields;
};

/** JSON for PATCH /admin/scheduled-emails/:id. Clears what no longer applies. */
export const scheduleToPatch = (s) => {
  const repeating = s.repeat !== "none";
  return {
    repeat: s.repeat,
    interval: repeating ? Number(s.interval) || 1 : 1,
    weekdays: s.repeat === "weekly" ? sortedDays(s.weekdays) : [],
    start_date: s.startDate,
    send_time: s.sendTime,
    end_date: repeating && s.ends === "on" && s.endDate ? s.endDate : null,
    max_runs: repeating && s.ends === "after" && s.maxRuns ? Number(s.maxRuns) : null,
  };
};

/** Editor state from a scheduled email the server returned. */
export const scheduleFromServer = (row) => ({
  repeat: row.repeat,
  interval: row.interval || 1,
  weekdays: row.weekdays || [],
  startDate: row.start_date,
  sendTime: row.send_time,
  ends: row.end_date ? "on" : row.max_runs ? "after" : "never",
  endDate: row.end_date || "",
  maxRuns: row.max_runs ? String(row.max_runs) : "",
});

const parseDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatDay = (iso, withWeekday = false) =>
  parseDate(iso).toLocaleDateString("en-US", {
    ...(withWeekday ? { weekday: "short" } : {}),
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export const formatTime = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h < 12 ? "AM" : "PM";
  return `${h % 12 || 12}:${pad(m)} ${suffix}`;
};

const joinWords = (words) =>
  words.length <= 1
    ? words.join("")
    : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;

/** "Every 2 weeks on Mon and Wed at 6:30 PM, until Dec 15, 2026" */
export const describeSchedule = (s) => {
  if (!s.startDate || !s.sendTime) return "";
  const at = `at ${formatTime(s.sendTime)}`;
  if (s.repeat === "none") return `Once on ${formatDay(s.startDate, true)} ${at}`;

  const n = Number(s.interval) || 1;
  const unit = UNIT[s.repeat];
  let text = n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`;
  if (s.repeat === "weekly") {
    const names = sortedDays(s.weekdays).map((d) => WEEKDAYS[d].short);
    text += ` on ${joinWords(names)}`;
  }
  if (s.repeat === "monthly") text += ` on day ${parseDate(s.startDate).getDate()}`;
  text += ` ${at}`;
  if (s.ends === "on" && s.endDate) text += `, until ${formatDay(s.endDate)}`;
  if (s.ends === "after" && s.maxRuns) {
    text += Number(s.maxRuns) === 1 ? ", once" : `, ${s.maxRuns} times`;
  }
  return text;
};

/** Why this schedule can't be saved, or null. */
export const scheduleError = (s, now = new Date()) => {
  if (!s.startDate) return "Pick a date";
  if (!/^\d{2}:\d{2}$/.test(s.sendTime || "")) return "Pick a time";
  if (s.repeat === "none") {
    const [h, m] = s.sendTime.split(":").map(Number);
    const when = parseDate(s.startDate);
    when.setHours(h, m, 0, 0);
    if (when <= now) return "That time is in the past";
    return null;
  }
  const n = Number(s.interval);
  if (!Number.isInteger(n) || n < 1 || n > 99) return "Repeat every must be between 1 and 99";
  if (s.repeat === "weekly" && s.weekdays.length === 0) return "Pick at least one day of the week";
  if (s.ends === "on") {
    if (!s.endDate) return "Pick the end date";
    if (s.endDate < s.startDate) return "The end date is before the start date";
  }
  if (s.ends === "after" && !(Number(s.maxRuns) >= 1)) return "Number of sends must be at least 1";
  return null;
};
